import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { storesApi } from '../../../api/stores.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMPTY = {
  code: '',
  name: '',
  address: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'IT',
  phone: '',
  email: '',
  active: 'true'
};

export function StoreFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    reset(initial ? {
      code: initial.code || '',
      name: initial.name || '',
      address: initial.address || '',
      city: initial.city || '',
      province: initial.province || '',
      postalCode: initial.postalCode || '',
      country: initial.country || 'IT',
      phone: initial.phone || '',
      email: initial.email || '',
      active: String(initial.active !== false)
    } : EMPTY);
  }, [open, initial, reset]);

  const mutation = useMutation({
    mutationFn: async (values) => {
      const payload = {
        ...values,
        active: values.active === 'true',
        country: (values.country || 'IT').toUpperCase()
      };
      // Server schema rejects empty strings on optional fields where
      // null is the only allowed absence value. Drop blanks.
      for (const k of Object.keys(payload)) {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      }
      if (editing) {
        delete payload.code;
        return storesApi.update(initial._id, payload);
      }
      return storesApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Negozio aggiornato.' : 'Negozio creato.');
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) {
        toast.error(err instanceof ApiError ? err.message : t('common.error'));
      }
    }
  });

  const onSubmit = (values) => mutation.mutate(values);

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? 'Modifica negozio' : 'Nuovo negozio'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="store-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="store-form" className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="Codice"
          hint="Identificativo breve, es. pa-centro"
          disabled={editing}
          error={errors.code?.message}
          {...register('code', editing ? {} : {
            required: 'Codice richiesto',
            pattern: { value: /^[a-z0-9_-]{2,32}$/, message: 'minuscole, cifre, - o _ (2-32 caratteri)' }
          })}
        />
        <Input label="Nome" error={errors.name?.message} {...register('name', { required: 'Nome richiesto', minLength: 2 })} />
        <Input label="Indirizzo" error={errors.address?.message} {...register('address')} />
        <Input label="Città" error={errors.city?.message} {...register('city')} />
        <Input label="Provincia" error={errors.province?.message} {...register('province')} />
        <Input label="CAP" error={errors.postalCode?.message} {...register('postalCode')} />
        <Input label="Paese (ISO-2)" maxLength={2} error={errors.country?.message} {...register('country')} />
        <Input label="Telefono" error={errors.phone?.message} {...register('phone')} />
        <Input label="Email" type="email" error={errors.email?.message} {...register('email')} />
        <Select label="Stato" error={errors.active?.message} {...register('active')}>
          <option value="true">Attivo</option>
          <option value="false">Disattivato</option>
        </Select>
      </form>
    </Modal>
  );
}
